/** How the report pages write numbers, deltas and dates, in one place. */

import type { ByStore, Fmt, StoreKey } from '@/api/client'

export const STORES: StoreKey[] = ['appstore', 'play', 'admob']
export const STORE_LABEL: Record<StoreKey, string> = { appstore: 'App Store', play: 'Play', admob: 'AdMob' }
/** The series fills: one hue per store, the same on every chart. */
export const STORE_FILL: Record<StoreKey, string> = { appstore: 'bg-chart-1', play: 'bg-chart-3', admob: 'bg-chart-4' }
export const KIND_LABEL: Record<string, string> = { app: '유료 앱', iap: '인앱', sub: '구독', ads: '광고' }
export const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

const MINUS = '−'
const nf = (n: number, digits = 0) => n.toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits })

export function num(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined) return '—'
  return (n < 0 ? MINUS : '') + nf(Math.abs(n), digits)
}

/** ₩12,345; a negative reads −₩12,345. */
export function won(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return (n < 0 ? MINUS : '') + '₩' + nf(Math.round(Math.abs(n)))
}

/** A ratio (0.0207) as "2.07%". The contract's pct values are ratios. */
export function pct(r: number | null | undefined): string {
  if (r === null || r === undefined) return '—'
  return (r < 0 ? MINUS : '') + nf(Math.abs(r) * 100, 2) + '%'
}

export function fmtValue(fmt: Fmt, v: number | null | undefined): string {
  switch (fmt) {
    case 'krw':
      return won(v)
    case 'pct':
      return pct(v)
    case 'score':
      return num(v, 1)
    default:
      return num(v)
  }
}

/** The change from prev to cur as text, with its sign: "+₩1,200", "−6",
 *  "−0.30%p". Null when either side has no data or nothing moved. */
export function changeText(fmt: Fmt, cur: number | null | undefined, prev: number | null | undefined): string | null {
  if (cur === null || cur === undefined || prev === null || prev === undefined) return null
  const d = cur - prev
  const sign = d < 0 ? MINUS : '+'
  const a = Math.abs(d)
  let body: string
  switch (fmt) {
    case 'krw':
      body = '₩' + nf(Math.round(a))
      break
    case 'pct':
      body = nf(a * 100, 2) + '%p'
      break
    case 'score':
      body = nf(a, 1)
      break
    default:
      body = nf(a)
  }
  if (body.replace(/[^1-9]/g, '') === '') return null
  return sign + body
}

/** 'up' or 'down' for the colour: a drop is good when `goodUp` is false. */
export function changeTone(cur: number | null | undefined, prev: number | null | undefined, goodUp = true): 'up' | 'down' | null {
  if (cur === null || cur === undefined || prev === null || prev === undefined || cur === prev) return null
  return (cur > prev) === goodUp ? 'up' : 'down'
}

export function rankText(r: number): string {
  return r > 0 ? `#${r}` : '순위 밖'
}

/** "10/20" from "2026-10-20". */
export function md(date: string | null | undefined): string {
  if (!date) return '—'
  return `${date.slice(5, 7)}/${date.slice(8, 10)}`
}

export function weekday(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  return WEEKDAYS[d.getUTCDay()]
}

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Today in Seoul, YYYY-MM-DD (KST is a fixed UTC+9). */
export function kstToday(now = new Date()): string {
  return new Date(now.getTime() + 9 * 3_600_000).toISOString().slice(0, 10)
}

export function sumStore(b: ByStore | undefined): number {
  return b ? b.appstore + b.play + b.admob : 0
}

export function storeLine(b: ByStore): string {
  return STORES.map((k) => `${STORE_LABEL[k]} ${won(b[k])}`).join(' · ')
}

/** How the trigger reads in the header line. */
export function triggerText(trigger: string): string {
  const t = trigger.toLowerCase()
  if (t === 'settle') return 'App Store 반영'
  if (t === '' || /sched|cron|auto/.test(t)) return '23:00 생성'
  if (/manual|build|rebuild|button/.test(t)) return '수동 생성'
  return `${trigger} 생성`
}
